<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';
import {
  CLIENT_API_PHOTO_GALLERY_KEY,
  CLIENT_API_PHOTO_GALLERY_URL,
  CLIENT_API_VIDEO_GALLERY_KEY,
  CLIENT_API_VIDEO_GALLERY_URL,
} from '~/shared/api/client/galleries/galleries.const';
import type {
  GalleryPaginatedResponse,
  PhotoGalleryDTO,
  VideoGalleryDTO,
} from '~/shared/api/client/galleries/galleries.dto';
import {
  GalleryCard,
  formatGalleryDate,
  getGalleryCover,
} from '~/entities/gallery-card';
import {
  EVENT_MEDIA_MODES,
  EVENTS_GALLERY_DETAIL_ROUTE_NAMES,
} from '~/shared/lib/events-routes.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { useEventsFilters } from '../../lib/useEventsFilters';
import { useEventsGalleriesCollectionSeo } from '../../lib/seo';
import EventsGalleryHeader from './EventsGalleryHeader.vue';

type GalleryItem = PhotoGalleryDTO | VideoGalleryDTO;

const PAGE_SIZE = 20;
const CURRENT_YEAR = new Date().getFullYear();
// Earliest year of the imported photo reports archive
const MIN_YEAR = 2006;

const { mediaMode, setMediaMode } = useEventsFilters();
const mode = computed({
  get: () => mediaMode.value,
  set: setMediaMode,
});
const year = ref(CURRENT_YEAR);
const page = ref(1);

const apiUrl = computed(() =>
  mode.value === EVENT_MEDIA_MODES.photos
    ? CLIENT_API_PHOTO_GALLERY_URL
    : CLIENT_API_VIDEO_GALLERY_URL,
);

const fetchKey = computed(() =>
  mode.value === EVENT_MEDIA_MODES.photos
    ? CLIENT_API_PHOTO_GALLERY_KEY
    : CLIENT_API_VIDEO_GALLERY_KEY,
);

const query = computed(() => ({
  page: page.value,
  page_size: PAGE_SIZE,
  year: year.value,
}));

// Items of already loaded pages; the current page comes straight from `data`,
// so the list is rendered on SSR and on first load without waiting for a watcher.
const previousPagesItems = ref<GalleryItem[]>([]);

watch([mode, year], () => {
  page.value = 1;
  previousPagesItems.value = [];
});

const { data, status } = useCustomFetch<GalleryPaginatedResponse<GalleryItem>>(
  () => apiUrl.value,
  {
    key: fetchKey.value,
    query,
  },
);

const accumulatedItems = computed<GalleryItem[]>(() => {
  // While a request is pending, `data` still holds the previous response:
  // the previous page on "load more", another year or mode on switch
  if (status.value === 'pending') {
    return previousPagesItems.value;
  }

  return [...previousPagesItems.value, ...(data.value?.results ?? [])];
});

useEventsGalleriesCollectionSeo({
  items: computed(() => data.value?.results ?? []),
  mode,
});

function loadMore(): void {
  if (status.value === 'pending' || !data.value?.next) {
    return;
  }
  previousPagesItems.value = accumulatedItems.value;
  page.value += 1;
}

function prevYear(): void {
  if (year.value <= MIN_YEAR) {
    return;
  }
  year.value -= 1;
}

function nextYear(): void {
  if (year.value >= CURRENT_YEAR) {
    return;
  }
  year.value += 1;
}

function getGalleryRoute(item: GalleryItem): RouteLocationRaw {
  const name =
    mode.value === EVENT_MEDIA_MODES.photos
      ? EVENTS_GALLERY_DETAIL_ROUTE_NAMES.photos
      : EVENTS_GALLERY_DETAIL_ROUTE_NAMES.videos;

  return {
    name,
    params: { id: item.id },
  };
}
</script>

<template>
  <div :class="$style.root">
    <EventsGalleryHeader
      v-model:mode="mode"
      :year="year"
      :can-prev="year > MIN_YEAR"
      :can-next="year < CURRENT_YEAR"
      @prev-year="prevYear"
      @next-year="nextYear"
    />

    <div v-if="accumulatedItems.length > 0" :class="$style.grid">
      <GalleryCard
        v-for="item in accumulatedItems"
        :key="item.id"
        :image-url="getGalleryCover(item)"
        :caption="formatGalleryDate(item)"
        :title="item.description ?? item.name"
        :to="getGalleryRoute(item)"
      />
    </div>

    <div v-else-if="status === 'pending'" :class="$style.state">
      <Loader />
    </div>

    <div v-else :class="$style.state">
      <p :class="$style.state__text">Нет материалов за {{ year }} год</p>
    </div>

    <div v-if="data?.next" :class="$style.footer">
      <Button
        theme="secondary"
        size="xl"
        :is-loading="status === 'pending'"
        :class="$style.loadMore"
        @click="loadMore"
      >
        Загрузить еще
      </Button>
    </div>
  </div>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  border-right: none;
  border-top: none;
}

.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);

  > :nth-child(4n) {
    border-right: none;
  }
}

.state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
}

.state__text {
  @include text_body1;
  color: var(--color-gray-text);
}

.footer {
  padding: 20px;
  border-top: 1px solid var(--color-gray-line);
}

.loadMore {
  width: 100%;
  background-color: var(--color-gray-bg);

  &:hover {
    background-color: var(--color-gray-line);
  }
}

@media (max-width: 1379px) {
  .grid {
    grid-template-columns: repeat(3, 1fr);

    > :nth-child(4n) {
      border-right: 1px solid var(--color-gray-line);
    }

    > :nth-child(3n) {
      border-right: none;
    }
  }
}
@media (max-width: 767px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);

    > :nth-child(3n) {
      border-right: 1px solid var(--color-gray-line);
    }

    > :nth-child(2n) {
      border-right: none;
    }
  }
}

@media (max-width: 479px) {
  .grid {
    grid-template-columns: 1fr;

    > :nth-child(n) {
      border-right: none;
    }
  }
}
</style>
